import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress
} from '@mui/material';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import axiosInstance from '../../utils/axios';
import { RootState } from '../../store';

const validationSchema = Yup.object({
  firstName: Yup.string().required('El nombre es requerido'),
  lastName: Yup.string().required('El apellido es requerido'),
  email: Yup.string().required('El usuario es requerido'),
  currentPassword: Yup.string(),
  newPassword: Yup.string().min(6, 'La contraseña debe tener al menos 6 caracteres'),
  confirmPassword: Yup.string().oneOf([Yup.ref('newPassword')], 'Las contraseñas no coinciden')
}).test('password-change', 'La contraseña actual es requerida para cambiar la contraseña', function(value) {
  const { newPassword, currentPassword } = value;
  if (newPassword && !currentPassword) {
    return false;
  }
  return true;
});

const Profile: React.FC = () => {
  const { user } = useSelector((state: RootState) => state.auth);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const handleSubmit = async (values: any) => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      // Actualizar datos básicos
      await axiosInstance.put('/users/profile/simple', {
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email
      });

      // Si se quiere cambiar la contraseña
      if (values.newPassword && values.currentPassword) {
        await axiosInstance.put('/users/profile/password', {
          currentPassword: values.currentPassword,
          newPassword: values.newPassword
        });
      }

      setSuccess('Perfil actualizado exitosamente');
    } catch (error: any) {
      setError(error.response?.data?.message || 'Error al actualizar el perfil');
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return null;
  }

  return (
    <Box sx={{ maxWidth: 600, mx: 'auto', mt: 4, px: 2 }}>
      <Card>
        <CardContent>
          <Typography variant="h5" gutterBottom>
            Mi Perfil
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          <Formik
            initialValues={{
              firstName: user.firstName,
              lastName: user.lastName,
              email: user.email,
              currentPassword: '',
              newPassword: '',
              confirmPassword: ''
            }}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ errors, touched }) => (
              <Form>
                <Field
                  as={TextField}
                  fullWidth
                  margin="normal"
                  name="firstName"
                  label="Nombre"
                  error={touched.firstName && Boolean(errors.firstName)}
                  helperText={touched.firstName && errors.firstName}
                />

                <Field
                  as={TextField}
                  fullWidth
                  margin="normal"
                  name="lastName"
                  label="Apellido"
                  error={touched.lastName && Boolean(errors.lastName)}
                  helperText={touched.lastName && errors.lastName}
                />

                <Field
                  as={TextField}
                  fullWidth
                  margin="normal"
                  name="email"
                  label="Usuario"
                  error={touched.email && Boolean(errors.email)}
                  helperText={touched.email && errors.email}
                />

                <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
                  Cambiar Contraseña
                </Typography>

                <Field
                  as={TextField}
                  fullWidth
                  margin="normal"
                  name="currentPassword"
                  label="Contraseña Actual"
                  type="password"
                  error={touched.currentPassword && Boolean(errors.currentPassword)}
                  helperText={touched.currentPassword && errors.currentPassword}
                />

                <Field
                  as={TextField}
                  fullWidth
                  margin="normal"
                  name="newPassword"
                  label="Nueva Contraseña"
                  type="password"
                  error={touched.newPassword && Boolean(errors.newPassword)}
                  helperText={touched.newPassword && errors.newPassword}
                />

                <Field
                  as={TextField}
                  fullWidth
                  margin="normal"
                  name="confirmPassword"
                  label="Confirmar Nueva Contraseña"
                  type="password"
                  error={touched.confirmPassword && Boolean(errors.confirmPassword)}
                  helperText={touched.confirmPassword && errors.confirmPassword}
                />

                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  fullWidth
                  sx={{ mt: 3 }}
                  disabled={loading}
                >
                  {loading ? <CircularProgress size={24} /> : 'Guardar Cambios'}
                </Button>
              </Form>
            )}
          </Formik>
        </CardContent>
      </Card>
    </Box>
  );
};

export default Profile; 